import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { sessionsAPI, trainerAPI } from '../../api';
import { formatDateTime, getSessionTypeLabel } from '../../utils';
import { Card, Button, Input, Select, Badge, Modal, Spinner, EmptyState } from '../../components/ui';
import { Calendar, Plus, Check } from 'lucide-react';

export const SessionsPage = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessions, setSessions] = useState([]);
  const [pendingSessions, setPendingSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    client_id: '',
    session_date: new Date().toISOString().slice(0, 16),
    duration_minutes: 60,
    session_type: 'personal_training',
    notes: '',
    pr_flag: false
  });

  const loadData = useCallback(async () => {
    try {
      const [sessionsRes] = await Promise.all([sessionsAPI.list()]);
      setSessions(sessionsRes.data);

      if (user?.role === 'client') {
        const pendingRes = await sessionsAPI.getPending();
        setPendingSessions(pendingRes.data);
      }

      if (user?.role === 'trainer' || user?.role === 'admin') {
        const clientsRes = await trainerAPI.getClients();
        setClients(clientsRes.data);
      }
    } catch (err) {
      console.error('Failed to load sessions:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle client query parameter from "Log Session" button
  useEffect(() => {
    const clientId = searchParams.get('client');
    if (clientId && clients.length > 0) {
      setForm(prev => ({ ...prev, client_id: clientId }));
      setShowModal(true);
      // Clear the query parameter after handling it
      searchParams.delete('client');
      setSearchParams(searchParams);
    }
  }, [searchParams, clients, setSearchParams]);

  const handleCreateSession = async (e) => {
    e.preventDefault();

    // Validate form
    if (!form.client_id) {
      alert('Please select a client');
      return;
    }

    setSubmitting(true);
    try {
      console.log('Creating session with data:', {
        ...form,
        session_date: new Date(form.session_date).toISOString()
      });

      await sessionsAPI.create({
        ...form,
        session_date: new Date(form.session_date).toISOString()
      });

      console.log('Session created successfully');
      alert('Session logged successfully!');
      setShowModal(false);
      setForm({
        client_id: '',
        session_date: new Date().toISOString().slice(0, 16),
        duration_minutes: 60,
        session_type: 'personal_training',
        notes: '',
        pr_flag: false
      });
      loadData();
    } catch (err) {
      console.error('Error creating session:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to create session';
      alert(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async (sessionId) => {
    try {
      await sessionsAPI.confirm(sessionId);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to confirm session');
    }
  };

  const canCreateSession = user?.role === 'trainer' || user?.role === 'admin';

  return (
    <div className="space-y-6" data-testid="sessions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Training Sessions</h1>
          <p className="text-gray-500">
            {user?.role === 'client' ? 'View and confirm your sessions' : 'Log and manage training sessions'}
          </p>
        </div>
        {canCreateSession && (
          <Button onClick={() => setShowModal(true)} data-testid="create-session-btn">
            <Plus className="w-4 h-4 mr-2" /> Log Session
          </Button>
        )}
      </div>

      {/* Pending Confirmations for Clients */}
      {user?.role === 'client' && pendingSessions.length > 0 && (
        <Card className="p-4 bg-amber-50 border-amber-200">
          <h3 className="font-semibold text-amber-900 mb-3">Pending Confirmations</h3>
          <div className="space-y-3">
            {pendingSessions.map(session => (
              <div key={session.id} className="bg-white p-4 rounded-lg flex items-center justify-between">
                <div>
                  <p className="font-medium">{getSessionTypeLabel(session.session_type)}</p>
                  <p className="text-sm text-gray-500">
                    {formatDateTime(session.session_date)} • {session.duration_minutes} min
                  </p>
                  {session.trainer_name && (
                    <p className="text-xs text-gray-400">with {session.trainer_name}</p>
                  )}
                </div>
                <Button onClick={() => handleConfirm(session.id)} size="sm" data-testid={`confirm-session-${session.id}`}>
                  <Check className="w-4 h-4 mr-1" /> Confirm
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Session History */}
      <Card>
        <div className="p-4 border-b">
          <h3 className="font-semibold">Session History</h3>
        </div>
        {loading ? (
          <div className="p-8 text-center"><Spinner /></div>
        ) : sessions.length === 0 ? (
          <EmptyState
            icon={Calendar}
            title="No sessions yet"
            description={canCreateSession ? "Log your first training session" : "Your sessions will appear here"}
            action={canCreateSession && <Button onClick={() => setShowModal(true)}>Log First Session</Button>}
          />
        ) : (
          <div className="divide-y">
            {sessions.map(session => (
              <div key={session.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{getSessionTypeLabel(session.session_type)}</p>
                      {session.pr_flag && <Badge variant="warning">PR!</Badge>}
                    </div>
                    <p className="text-sm text-gray-500">
                      {formatDateTime(session.session_date)} • {session.duration_minutes} min
                    </p>
                    {session.notes && <p className="text-sm text-gray-600 mt-1">{session.notes}</p>}
                    {user?.role !== 'client' && session.client_name && (
                      <p className="text-xs text-gray-400 mt-1">Client: {session.client_name}</p>
                    )}
                    {user?.role === 'client' && session.trainer_name && (
                      <p className="text-xs text-gray-400 mt-1">Trainer: {session.trainer_name}</p>
                    )}
                  </div>
                  <Badge variant={session.status === 'confirmed' ? 'success' : session.status === 'pending' ? 'warning' : 'danger'}>
                    {session.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Create Session Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Log Training Session">
        <form onSubmit={handleCreateSession} className="space-y-4">
          <Select
            label="Client"
            value={form.client_id}
            onChange={e => setForm({ ...form, client_id: e.target.value })}
            options={[
              { value: '', label: 'Select client...' },
              ...clients.map(c => ({ value: c.client_id, label: c.client_name }))
            ]}
            required
          />
          <Input
            label="Date & Time"
            type="datetime-local"
            value={form.session_date}
            onChange={e => setForm({ ...form, session_date: e.target.value })}
            required
          />
          <Input
            label="Duration (minutes)"
            type="number"
            value={form.duration_minutes}
            onChange={e => setForm({ ...form, duration_minutes: parseInt(e.target.value) })}
            min={1}
            max={480}
            required
          />
          <Select
            label="Session Type"
            value={form.session_type}
            onChange={e => setForm({ ...form, session_type: e.target.value })}
            options={[
              { value: 'personal_training', label: 'Personal Training' },
              { value: 'group_class', label: 'Group Class' },
              { value: 'assessment', label: 'Assessment' },
              { value: 'strength', label: 'Strength Training' },
              { value: 'cardio', label: 'Cardio' },
              { value: 'flexibility', label: 'Flexibility' },
              { value: 'rehabilitation', label: 'Rehabilitation' },
            ]}
          />
          <Input
            label="Notes"
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            placeholder="Session notes..."
          />
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.pr_flag}
              onChange={e => setForm({ ...form, pr_flag: e.target.checked })}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm">Personal Record (PR) achieved</span>
          </label>
          <div className="flex gap-3">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)} className="flex-1" disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" data-testid="submit-session" disabled={submitting}>
              {submitting ? 'Logging...' : 'Log Session'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default SessionsPage;
